import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

const footerLinks = {
  Product: [
    { label: 'For Business', href: '#business' },
    { label: 'For Personal', href: '#personal' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Examples', href: '/examples' },
  ],
  Tools: [
    { label: 'Design Editor', href: '/create' },
    { label: 'QR Code Studio', href: '/qr-codes' },
    { label: 'Campaign Generator', href: '/campaigns' },
    { label: 'Brand Kit', href: '/brands' },
  ],
  Company: [
    { label: 'About Us', href: '/contact' },
    { label: 'Contact', href: '/contact' },
  ],
  Legal: [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Cookie Policy', href: '/cookies' },
    { label: 'Acceptable Use', href: '/acceptable-use' },
  ],
};

function InstagramIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" />
    </svg>
  );
}

function XIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function LinkedinIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

const socials = [
  { Icon: InstagramIcon, label: 'Instagram', href: 'https://instagram.com' },
  { Icon: XIcon, label: 'X (Twitter)', href: 'https://x.com' },
  { Icon: LinkedinIcon, label: 'LinkedIn', href: 'https://linkedin.com' },
];

export default function HomepageFooter() {
  return (
    <footer className="border-t border-border bg-card/50">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-6 sm:gap-8 mb-10">
          <div className="col-span-2 md:col-span-3 xl:col-span-2">
            <Link href="/" className="flex items-center gap-2.5 mb-4">
              <AppLogo size={28} />
              <span className="font-bold text-lg">
                DESIGNHUB <span className="text-gradient-gold">AI</span>
              </span>
            </Link>
            <p className="text-sm text-slate-300 leading-relaxed mb-5 max-w-xs">
              AI-powered creative platform for businesses and personal users.
              Create everything you need — designs, campaigns, videos, and more.
            </p>
            <div className="flex items-center gap-3">
              {socials?.map((s) => (
                <a
                  key={`social-${s?.label}`}
                  href={s?.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s?.label}
                  className="w-9 h-9 rounded-xl bg-surface-3 border border-border flex items-center justify-center text-slate-400 hover:text-foreground hover:border-primary/30 transition-all"
                >
                  <s.Icon size={15} />
                </a>
              ))}
            </div>
          </div>

          {Object.entries(footerLinks)?.map(([section, links]) => (
            <div key={`footer-col-${section}`}>
              <h4 className="text-xs font-semibold text-foreground uppercase tracking-widest mb-4">{section}</h4>
              <ul className="space-y-2.5">
                {links?.map((link) => (
                  <li key={`footer-link-${section}-${link?.label}`}>
                    {link.href.startsWith('#') ? (
                      <a href={link.href} className="text-sm text-slate-300 hover:text-foreground transition-colors">
                        {link?.label}
                      </a>
                    ) : (
                      <Link href={link.href} className="text-sm text-slate-300 hover:text-foreground transition-colors">
                        {link?.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-400">
            © 2026 DESIGNHUB AI. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <span>Designed with privacy in mind</span>
            <span>🇳🇴 Norway · NOK</span>
          </div>
        </div>
      </div>
    </footer>
  );
}